import { Box, Stack, Typography } from "@mui/material";

// The kit's own, so the trigger takes focus. MUI's Tooltip opens on
// hover and on focus, and a Chip, a Box or a Typography takes neither.
import Tooltip from "./Tooltip.jsx";

/** The widest a single bucket is allowed to get, and the gap between. */
const BAR = 56;
const GAP = 3;

/**
 * Activity over time, stacked by whoever did it.
 *
 * ## Why the series are a ramp and not a rainbow
 *
 * The obvious way to colour six writers is six hues, and this product cannot
 * have them. Green, red, amber and violet are spoken for — permitted,
 * refused, attention, unattended — and a chart that drew `xero` in green
 * would make a permitted tag stop reading as a signal three screens away.
 * Semantic colour only works while it is scarce.
 *
 * So the series are steps along the brand ramp plus one neutral, which is a
 * palette that cannot be mistaken for a state because no state is ever teal.
 * The cost is that two adjacent series are closer than two arbitrary hues
 * would be, and it is paid back by the rules below: **the legend carries the
 * name and the count**, every segment names its series in a tooltip, and the
 * stacking order is the legend order. Colour is the fourth way to tell them
 * apart here, not the first.
 *
 * ## What it is of
 *
 * The rows handed to it, and the caller says so in the caption. This draws no
 * axis it did not measure: the only y-value printed is the tallest bucket,
 * because that is the one number the shape is actually scaled against.
 *
 * An empty bucket is drawn as a hairline rather than skipped — a gap in a bar
 * chart reads as "no data" and a flat zero reads as "nothing happened", and
 * those are different facts everywhere else in this product too.
 */
export default function StackedBars({
  buckets = [],
  series = [],
  colours = [],
  height = 150,
  caption,
  label,
}) {
  if (!buckets.length || !series.length) return null;

  const totals = buckets.map((bucket) =>
    series.reduce((n, key) => n + (bucket.values[key] || 0), 0),
  );
  const ceiling = Math.max(...totals, 1);

  const colourFor = (index) => colours[index % colours.length];

  // The whole thing shrinks to the bars rather than the bars floating in it.
  // Capping the bar width and leaving the container full-width left the end
  // label stranded two thirds of the page away from the last bar it names,
  // which reads as a broken axis rather than as a short series.
  const width = `min(100%, ${buckets.length * (BAR + GAP)}px)`;

  return (
    <Box sx={{ minWidth: 0, width }}>
      <Box
        role="img"
        aria-label={
          label
            || `Stacked bars for ${buckets.length} buckets and ${series.length} writers. The busiest holds ${ceiling}.`
        }
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: `${GAP}px`,
          height,
          // Capped. Seven daily buckets stretched across a thousand pixels
          // are seven slabs, and a slab is not a bar — the eye reads width as
          // meaning and there is none here. A bar chart that runs out of data
          // should get shorter, not fatter.
          "& > *": { maxWidth: BAR },
        }}
      >
        {buckets.map((bucket, index) => {
          const total = totals[index];
          return (
            <Box
              key={bucket.label}
              sx={{
                flex: 1,
                minWidth: 2,
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                gap: "1px",
              }}
            >
              {total === 0 ? (
                <Tooltip title={`${bucket.label} — nothing`}>
                  <Box
                    sx={{
                      height: 2,
                      borderRadius: 0.5,
                      backgroundColor: "divider",
                    }}
                  />
                </Tooltip>
              ) : (
                series.map((key, s) =>
                  bucket.values[key] ? (
                    <Tooltip
                      key={key}
                      title={`${bucket.label} · ${key} — ${bucket.values[key].toLocaleString()}`}
                    >
                      <Box
                        sx={{
                          height: `${(bucket.values[key] / ceiling) * 100}%`,
                          minHeight: 2,
                          borderRadius: 0.5,
                          backgroundColor: colourFor(s),
                          transition: "height 220ms ease-out",
                          "@media (prefers-reduced-motion: reduce)": {
                            transition: "none",
                          },
                          "&:hover": { opacity: 0.8 },
                        }}
                      />
                    </Tooltip>
                  ) : null,
                )
              )}
            </Box>
          );
        })}
      </Box>

      {/* The scale, stated rather than drawn. A printed axis on a chart this
          small is four numbers nobody reads; the tallest bucket is the only
          one the bars are actually measured against. */}
      <Stack
        direction="row"
        sx={{ justifyContent: "space-between", mt: 0.75, gap: 1 }}
      >
        <Typography variant="caption" color="text.secondary">
          {buckets[0].label}
        </Typography>
        {caption ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ textAlign: "center" }}
          >
            {caption}
          </Typography>
        ) : null}
        <Typography variant="caption" color="text.secondary">
          {buckets[buckets.length - 1].label}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * The legend, which is where the names actually live.
 *
 * Not decoration and not optional. The series are steps along one ramp
 * precisely because hue is not available here, so this is the thing doing the
 * work of telling six writers apart — the swatch is the reminder and the name
 * is the answer.
 *
 * Counts are on it for the same reason a `Stat` label carries its basis: a
 * legend that says only `xero` invites a reader to guess its share off the
 * bar, and the number is right there to be printed.
 */
export function Legend({ series = [], counts = {}, colours = [], onSelect, selected }) {
  return (
    <Stack
      direction="row"
      sx={{ flexWrap: "wrap", columnGap: 2, rowGap: 0.75, mt: 1.5 }}
    >
      {series.map((key, index) => {
        const chosen = selected === key;
        return (
          <Stack
            key={key}
            direction="row"
            spacing={0.75}
            component={onSelect ? "button" : "div"}
            type={onSelect ? "button" : undefined}
            onClick={onSelect ? () => onSelect(chosen ? "" : key) : undefined}
            sx={{
              alignItems: "center",
              minWidth: 0,
              p: 0,
              border: 0,
              background: "none",
              font: "inherit",
              color: "inherit",
              cursor: onSelect ? "pointer" : "default",
              opacity: selected && !chosen ? 0.45 : 1,
              transition: "opacity 120ms",
              "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 0.5,
                flexShrink: 0,
                backgroundColor: colours[index % colours.length],
              }}
            />
            <Typography
              variant="body2"
              sx={{ fontWeight: chosen ? 600 : 400, overflowWrap: "anywhere" }}
            >
              {key}
            </Typography>
            {counts[key] != null ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontVariantNumeric: "tabular-nums" }}
              >
                {counts[key].toLocaleString()}
              </Typography>
            ) : null}
          </Stack>
        );
      })}
    </Stack>
  );
}
